import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';

/**
 * VFBadge — badges statut pour la table Dialer.
 */
export type BadgeStatus = 'new' | 'contacted' | 'qualified' | 'callback' | 'booked' | 'not_interested' | 'lost';

interface StatusMeta {
  var: string;
}

const STATUS_MAP: Record<BadgeStatus, StatusMeta> = {
  new:            { var: 'new' },
  contacted:      { var: 'contacted' },
  qualified:      { var: 'qualified' },
  callback:       { var: 'callback' },
  booked:         { var: 'booked' },
  not_interested: { var: 'lost' },
  lost:           { var: 'lost' },
};

interface VFBadgeProps {
  status?: BadgeStatus | string;
  label?: ReactNode;
  className?: string;
  size?: 'sm' | 'md';
}

export function VFBadge({ status, label, className = '', size = 'md' }: VFBadgeProps) {
  const { t } = useTranslation('dialer');
  const meta = status ? STATUS_MAP[status as BadgeStatus] : undefined;
  if (!meta) {
    return (
      <span className={`inline-flex items-center rounded-vf-sm px-2 ${size === 'sm' ? 'h-5 text-[10px]' : 'h-6 text-[11px]'} font-medium bg-[var(--vf-surface-3)] text-[var(--vf-text-2)] ${className}`}>
        {label || status}
      </span>
    );
  }
  const style = {
    background: `var(--vf-badge-${meta.var}-bg)`,
    color:      `var(--vf-badge-${meta.var}-tx)`,
  };
  return (
    <span
      className={`inline-flex items-center rounded-vf-sm px-2 ${size === 'sm' ? 'h-5 text-[10px]' : 'h-6 text-[11px]'} font-medium ${className}`}
      style={style}
    >
      {label || outcomeLabel(status, t)}
    </span>
  );
}

export type ToneColor = 'green' | 'orange' | 'red' | 'blue' | 'yellow' | 'pink' | 'accent';

interface VFToneProps {
  tone?: ToneColor;
  children?: ReactNode;
  className?: string;
  icon?: ReactNode;
}

/** VFTone — badge neutre piloté par un token semantic (green/orange/red/blue/yellow/pink). */
export function VFTone({ tone = 'blue', children, className = '', icon }: VFToneProps) {
  const style = {
    background: `var(--vf-${tone}-bg)`,
    color:      `var(--vf-${tone})`,
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-vf-sm px-2 h-6 text-[11px] font-medium ${className}`}
      style={style}
    >
      {icon}
      {children}
    </span>
  );
}

export default VFBadge;
